﻿//==========================================================================================================
// Copyright (C) NoreTechnology AB. All Rights Reserved.
//==========================================================================================================

// This file contains a simple overlay to display a message and save/cancel/close buttons.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { useIntl } from "~base/Internationalization/IntlContext";
import Overlay from "~base/Components/Composite/Overlay";
import useOverlayStyles from "~base/Components/Composite/Overlay/OverlayStyle";
import { PromptOverlayProps } from "./Types";

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const Component: React.FunctionComponent<PromptOverlayProps> = ({
    open,
    portalContainer,
    onClose,
    onCancel,
    onSave,
    title,
    titleTextCode,
    bodyTextCode,
    bodyTextCodeL,
    bodyTextString,
    showSave = false,
    showClose = true,
    saveButtonEnabled = true,
    saveButtonTextCode = "VisiFinance.UI.Main.Overlay.SparaKnapp.Text",
    closeButtonTextCode = "VisiFinance.UI.Main.Overlay.StängKnapp.Text",
    style = { whiteSpace: "break-spaces" },
    children,
}) => {
    const intl = useIntl();
    const classes = useOverlayStyles();

    const key = React.useRef<HTMLDivElement>(undefined);

    const actualTitle = titleTextCode ? intl.formatText(titleTextCode) : title;

    const actualBodyText =
        bodyTextString ?? (bodyTextCode ? intl.formatText(bodyTextCode) : null);

    const actualBodyTextFromL = bodyTextCodeL
        ? bodyTextCodeL.map((fqn, i) => (
              <div key={i}>{intl.formatText(fqn)}</div>
          ))
        : null;

    const closeWrapper = () => {
        if (onClose) {
            onClose();
        }
    };

    const cancelWrapper = () => {
        if (onCancel) {
            onCancel();
        }
    };

    const saveWrapper = () => {
        if (onSave) {
            onSave();
        }
    };

    return (
        <Overlay
            divRef={key}
            open={open}
            onClose={() => closeWrapper()}
            onCancel={() => cancelWrapper()}
            onSave={() => saveWrapper()}
            title={actualTitle}
            saveButtonTitle={intl.formatText(saveButtonTextCode)}
            showSaveButton={showSave}
            abortButtonTitle={intl.formatText(closeButtonTextCode)}
            showAbortButton={showClose}
            overlayContainer={portalContainer}
            saveButtonEnabled={saveButtonEnabled}
            contentPadding={[{ dir: "All", size: "XL" }]}
            contentStyle={style}
            contentClassName={classes.defaultWidth}
        >
            <div className={classes.overlayContentText}>
                {actualBodyText}
                {actualBodyTextFromL}
            </div>
            {children}
        </Overlay>
    );
};

//-------------------------------------------------------------------------------------
// Exports
//-------------------------------------------------------------------------------------

export default Component;
export { Component };
